import remove from 'lodash/remove'

const elementsSelectors: Record<string, string> = {
  play: '.vjs-play-control',
  seekBackwards: '.vjs-seek-backward',
  seekForward: '.vjs-seek-forward',
  currentTime: '.vjs-current-time',
  timeDivider: '.vjs-time-divider',
  totalTime: '.vjs-duration',
  seekToLive: '.vjs-seek-to-live-control',
  totalViewers: '.vjs-total-viewers',
  volume: '.vjs-volume-panel-horizontal',
  volumeSlider: '.vjs-volume-horizontal',
  qualitySelector: '.vjs-quality-selector',
  pictureInPicture: '.vjs-picture-in-picture-control',
  chromecast: '.vjs-chromecast-button',
  fullscreen: '.vjs-fullscreen-control',
}

const controlElements = Object.keys(elementsSelectors)

const buttonHidingPriority: Record<string, number> = {
  pictureInPicture: 11,
  totalViewers: 10,
  volumeSlider: 9,
  timeDivider: 8,
  totalTime: 8,
  chromecast: 7,
  seekBackwards: 6,
  seekForward: 6,
  qualitySelector: 5,
  seekToLive: 4,
  currentTime: 3,
  volume: 2,
  fullscreen: 1,
  play: 0,
}
const hiddenElements: string[] = []
let currentPriority = 11
const elementsSizes: { [key: string]: number } = {}
let oldControlBarWidth = Infinity

export const optimizeControlBarSpace = (
  controlBar: any,
  isMobile?: boolean,
  atLiveEdge?: boolean,
  isLive?: boolean,
  timeLoaded?: boolean,
): void => {
  const controlBarWidth = controlBar.el().clientWidth

  if (isLive && atLiveEdge) {
    controlBar.durationDisplay.el()?.classList.add('vjs-hidden')
    controlBar.timeDivider.el()?.classList.add('vjs-hidden')
    controlBar.currentTimeDisplay.el()?.classList.add('vjs-hidden')
    hiddenElements.push('currentTime', 'timeDivider', 'totalTime')
  } else if (isLive) {
    controlBar.durationDisplay.el()?.classList.remove('vjs-hidden')
    controlBar.timeDivider.el()?.classList.remove('vjs-hidden')
    controlBar.currentTimeDisplay.el()?.classList.remove('vjs-hidden')
    remove(hiddenElements, (hiddenElement: string) =>
      ['currentTime', 'timeDivider', 'totalTime'].includes(hiddenElement),
    )
  }

  const isPlayerShrinking = oldControlBarWidth > controlBarWidth
  const isPlayerExpanding = oldControlBarWidth < controlBarWidth

  if (!isPlayerShrinking && !isPlayerExpanding && !timeLoaded) return

  let visibleElementsWidth = 0
  const elements = isMobile
    ? [...controlElements].filter(
        (element: string) =>
          !['play', 'seekBackwards', 'seekForward'].includes(element),
      )
    : [...controlElements]

  elements.map((element: string) => {
    const $element = controlBar.el().querySelector(elementsSelectors[element])

    if (!hiddenElements.includes(element)) {
      elementsSizes[element] =
        (element === 'volumeSlider' && 120 - (elementsSizes?.volume || 48)) || // always count volume slider
        (element === 'chromecast' && 36) || // always assume chromecast is available
        $element?.clientWidth ||
        0
      visibleElementsWidth += elementsSizes[element]
    }
  })

  elements.sort(sortElementsByPriorities(isPlayerShrinking)) //use reversed hiding priority for showing buttons when screen size grows

  elements.map((element: string) => {
    const isHiding =
      isPlayerShrinking &&
      shouldHide(element, visibleElementsWidth, controlBarWidth)
    const isShowing =
      isPlayerExpanding &&
      shouldShow(element, visibleElementsWidth, controlBarWidth)

    if (!isHiding && !isShowing) return

    const $element = controlBar.el().querySelector(elementsSelectors[element])

    if (isHiding) {
      $element?.classList?.add('vjs-hidden')
      element === 'volumeSlider' &&
        $element?.parentNode?.classList?.add('vjs-lock')

      hiddenElements.push(element)
      visibleElementsWidth -= elementsSizes[element]
    }

    if (isShowing) {
      $element?.classList?.remove('vjs-hidden')
      visibleElementsWidth += elementsSizes[element]
      element === 'volumeSlider' &&
        $element?.parentNode?.classList?.remove('vjs-lock')

      remove(
        hiddenElements,
        (hiddenElement: string) => hiddenElement === element,
      )
    }

    const priority = buttonHidingPriority[element]

    if (
      (isShowing || isHiding) &&
      !Object.keys(buttonHidingPriority).find(
        (item) => buttonHidingPriority[item] === priority && item !== element,
      )
    ) {
      currentPriority =
        buttonHidingPriority[element] +
        ((isHiding && -1) || (isShowing && 1) || 0)
    }
  })

  oldControlBarWidth = controlBarWidth
}

const sortElementsByPriorities =
  (ascending = true) =>
  (a: string, b: string): number => {
    return (
      ((buttonHidingPriority[a] || 0) - (buttonHidingPriority[b] || 0)) *
      (ascending ? -1 : 1)
    )
  }

const shouldHide = (
  element: string,
  visibleElementsWidth: number,
  controlBarWidth: number,
) => {
  return (
    currentPriority - 1 <= buttonHidingPriority[element] &&
    !hiddenElements.includes(element) &&
    visibleElementsWidth > controlBarWidth
  )
}

const shouldShow = (
  element: string,
  visibleElementsWidth: number,
  controlBarWidth: number,
) => {
  return (
    currentPriority + 1 >= buttonHidingPriority[element] &&
    hiddenElements.includes(element) &&
    visibleElementsWidth + elementsSizes[element] < controlBarWidth
  )
}
